import { GlowOrb } from './NavBar';
import { productImages } from '../images';

export default function ProductSection() {
  const products = [
    {
      image: productImages.cogImage,
      // 全身像:整张放进去(contain), 不裁 —— 裁了就只剩腿
      // 抠像:整张放进卡片(全身), 与下一张的面罩特写构图完全不同
      fit: 'object-contain',
      cutout: true,
      title: '认知与决策核心',
      subtitle: '人 · 指挥官',
      desc: '基于常识推理引擎与价值判断模型，支持临机决断与多目标权衡。将人类战略意图转化为可执行战术指令。',
      tags: ['常识推理', '价值判断', '临机决断'],
      color: 'primary',
    },
    {
      image: productImages.interfaceImage,
      // 没有独立的眼镜素材, 但这张里的人戴着 AR 面罩 —— 取头部特写, 既贴题
      // 又不会和上一张"同一个人全身像"撞脸。位置 50% 14% ≈ 面罩高度。
      // 真的 AR 眼镜图(原型那张, 已落地成本地文件)。它是黑底不是抠像,
      // 所以走 lighten + 遮罩那条路让黑场让位给卡片。
      fit: 'object-cover object-center',
      title: '交互与感知中枢',
      subtitle: '镜 · 智能眼镜',
      desc: '第一视角实时采集环境数据，AR 叠加空间标注与导航指引。将人机意图翻译为机器可理解的坐标与动作指令。',
      tags: ['第一视角', 'AR 显示', '意图翻译'],
      color: 'accent',
    },
    {
      image: productImages.actuationImage,
      fit: 'object-contain',
      cutout: true,
      title: '移动与执行终端',
      subtitle: '犬 · 机器狗',
      desc: '四足仿生运动结构，适应复杂地形与狭窄空间。搭载多模态传感器，实现厘米级定位与自主路径规划。',
      tags: ['四足运动', '多模态传感', '自主导航'],
      color: 'primary',
    },
    {
      image: productImages.robotImage,
      fit: 'object-contain',
      cutout: true,
      title: '人形服务终端',
      subtitle: '甲 · 机器人',
      desc: '双足人形结构，适配人类工作场景。支持精细操作、语音交互与情感识别，胜任接待、辅助与协作任务。',
      tags: ['双足行走', '精细操作', '语音交互'],
      color: 'accent',
    },
    {
      image: productImages.droneImage,
      fit: 'object-contain',
      cutout: true,
      title: '空中侦察终端',
      subtitle: '翼 · 无人机',
      desc: '多旋翼/固定翼可选，高空广角俯瞰全局。实时回传 4K 视频与热成像数据，支撑空地一体态势感知。',
      tags: ['高空俯瞰', '热成像', '空地联动'],
      color: 'primary',
    },
    {
      image: productImages.ugvImage,
      fit: 'object-contain',
      cutout: true,
      title: '地面运载终端',
      subtitle: '轮 · 无人车',
      desc: '轮式/履带式底盘，大载重长续航。负责物资运输、环境采样与长距离巡逻，是地面物流的主力节点。',
      tags: ['大载重', '长续航', '物流运输'],
      color: 'accent',
    },
  ];

  return (
    <section id="产品" className="reveal relative py-24 overflow-hidden">
      <GlowOrb className="top-0 right-0 w-[600px] h-[600px] bg-primary-500/5" />

      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <p className="text-xs font-mono tracking-[0.2em] text-primary-400 uppercase mb-3">
            Hybrid Intelligence Fleet
          </p>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-50 mb-4">
            「人 · 镜 · 犬 · 甲 · 翼 · 轮」异构终端舰队
          </h2>
          <p className="text-base text-foreground-400 max-w-2xl">
            六种异构终端在同一空间坐标系下共享感知、协同作业，形成完整的物理世界理解闭环。
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {products.map((p) => (
            <div key={p.subtitle} className="card-glow rounded-2xl">
              <div className="group h-full rounded-2xl border border-background-800/60 bg-background-900/40 hover:border-primary-500/30 transition-all overflow-visible">
                {/* 图与卡片的融合分两种走法:
                    · **抠像**(透明底):什么都不用做 —— 没有背景就没有矩形。
                      背后垫一层柔光, 主体不至于飘在纯色上。
                    · **原图**(只剩「镜」这一张, 因为没有眼镜素材):黑底比卡片更亮,
                      所以用 lighten 让黑场让位, 再用遮罩把四边化开。
                    走过的弯路记在这:先 cover 裁掉了头, 改 contain 又露出黑底板,
                    加混合模式只是遮掩 —— 换成抠像才是根治。 */}
                <div className="relative aspect-[16/10] overflow-hidden rounded-t-2xl">
                  {'cutout' in p && (p as { cutout?: boolean }).cutout && (
                    <div className="absolute inset-0 pointer-events-none"
                      style={{ background: 'radial-gradient(ellipse 55% 60% at 50% 55%, rgba(217,162,78,0.10), transparent 70%)' }} />
                  )}
                  <div className={`absolute inset-0 ${'zoom' in p ? (p as { zoom?: string }).zoom : ''}`}>
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className={`w-full h-full ${p.fit} group-hover:scale-[1.06] transition-transform duration-700 ${'cutout' in p && (p as { cutout?: boolean }).cutout ? 'drop-shadow-[0_10px_30px_rgba(0,0,0,0.55)]' : ''}`}
                    style={'cutout' in p && (p as { cutout?: boolean }).cutout ? undefined : {
                      mixBlendMode: 'lighten',
                      maskImage: 'radial-gradient(ellipse 96% 104% at 50% 38%, #000 42%, rgba(0,0,0,0.72) 72%, transparent 100%)',
                      WebkitMaskImage: 'radial-gradient(ellipse 96% 104% at 50% 38%, #000 42%, rgba(0,0,0,0.72) 72%, transparent 100%)',
                    }}
                  />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background-900/80 to-transparent pointer-events-none" />
                </div>
                <div className="p-5">
                  <p className="text-xs font-mono text-primary-400 mb-1.5">{p.subtitle}</p>
                  <h3 className="text-base font-semibold text-foreground-100 mb-2">{p.title}</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed mb-3">{p.desc}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.tags.map((tag) => (
                      <span
                        key={tag}
                        className={`px-2 py-0.5 rounded-full text-[10px] border ${
                          p.color === 'accent'
                            ? 'bg-accent-500/10 text-accent-300 border-accent-500/10'
                            : 'bg-primary-500/10 text-primary-300 border-primary-500/10'
                        }`}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}